import { Box, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import { tokens } from "../../../../theme";
import Shbutton from "../../../theme/buttons/Shbutton";
import SidepanelHeaderActiongroup from "../../../theme/sidepanel2/SidepanelHeaderActiongroup";
import Sidepanel2 from "../../../theme/sidepanel2/Sidepanel2";
import ShRowSkeleton from "../../../theme/skeleton/ShRowSkeleton";
import ShLabel from "../../../theme/label/ShLabel";
import { spEtailizeWebshopDetailsConfig } from "./spEtailizeWebshopDetailsConfig";
import Details from "./Views/Details";
import Sync from "./Views/Sync";
import Orders from "./Views/Orders";
import { useGetWebshopCredentialsSingleQuery } from "../../../../newapi/webshop/webshopSlice";
import { getWebshopLogoNoSize, marketPlaceAppIconsMap, normalizeMarketPlaceName } from "../../../../utils/logomapper";
import { ENV_TYPES } from "../../Sidebar";
import ButtonGroup from "../../../theme/buttons/ButtonGroup";

const SidePanelActions = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if(props.isRelation) return (<></>)
    return (
        <SidepanelHeaderActiongroup>
            <Shbutton 
                isCancel={true} 
                onClick={() => props.setIsExtended(false)} 
                color="primary" 
                variant="text" 
                startIcon={<ArrowRightAltIcon/>}
            >
                Cancel
            </Shbutton>
            <ButtonGroup version={2} isSidepanel>
                <Shbutton
                    onClick={() => window.location.href = `/warehouse/webshopcredentials/data?webshop=${props.etailizeWebshopId}`}
                    endIcon={<ArrowForwardIos/>}    
                    className={"group-btn"}
                    color={theme.palette.mode === "dark" ? "blue" :"primary"}
                    variant="contained"
                >
                    {t('overview')}
                </Shbutton>
                {/* <Shbutton
                    onClick={props.handleFilterPopoverClick}
                    endIcon={<ArrowDropDown/>}  
                    className={"group-btn"}
                    color={"primaryGrey"}
                    variant="contained"
                >
                    {t('actions')}
                </Shbutton> */}
            </ButtonGroup>
        </SidepanelHeaderActiongroup>
    )
}

const SpEtailizeWebshopDetails = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const config = spEtailizeWebshopDetailsConfig;

    const { data: etailizeWebshopData, isFetching, isLoading, isError } = useGetWebshopCredentialsSingleQuery(props.etailizeWebshopId, {
        skip: !props.etailizeWebshopId
    });
    // console.log("props.etailizeWebshopId",props.etailizeWebshopId);
    // console.log("etailizeWebshopData",etailizeWebshopData);
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === ENV_TYPES[200];

    // Actions dropdown state
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterPopoverOpen = Boolean(filterAnchorEl);
    const filterPopoverId = filterPopoverOpen ? 'simple-popover' : undefined;

    const handleFilterPopoverClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterPopoverClose = () => {
        setFilterAnchorEl(null);
    };

    const defaultIcon = useMemo(() => ({
        theme: theme.palette.mode === "dark" ? "blue" : "primary",
        content: <StorefrontOutlined/>
    }), [theme.palette.mode]);

    const defaultOptions = useMemo(() => [
        { component: <ShRowSkeleton heading={t('name')} /> },
        { component: <ShRowSkeleton heading={t('status')} /> },
        { component: <ShRowSkeleton heading={t('marketplace')} /> }
    ], [t]);

    const setIsExtendedCallback = useCallback(() => props.setIsExtended(false), [props.setIsExtended]);

    const defaultState = useMemo(() => ({
        title: "",
        subTitle: "",
        icon: defaultIcon,
        options: defaultOptions,
        actions: <SidePanelActions 
            setIsExtended={setIsExtendedCallback} 
            handleFilterPopoverClick={handleFilterPopoverClick}
            etailizeWebshopId={props.etailizeWebshopId}
            isRelation={isRelation}
        />
    }), [defaultIcon, defaultOptions, setIsExtendedCallback]);

    const [panel, setPanel] = useState(defaultState);

    useEffect(() => {
        setPanel(defaultState);
    }, [isFetching, props.etailizeWebshopId, theme.palette.mode]);

    useEffect(() => {
        if (!isLoading && !isFetching && etailizeWebshopData) {
            const isActive = etailizeWebshopData.state === 'active';
            const marketplaceName = etailizeWebshopData.credentials?.marketplace_config?.name || etailizeWebshopData.webshop_type;

            const normName = normalizeMarketPlaceName(marketplaceName).toLowerCase();
            const match = marketPlaceAppIconsMap.find(icon => normName.includes(icon.name));

            const etailizeIcon = {
                content: <StorefrontOutlined/>,
            };

            setPanel(prevPanel => ({
                ...prevPanel,
                beforeChild:(
                    <Box 
                        sx={{
                            mb:3,
                            "@media screen and (max-width: 63.9375em)" : {
                                display:"none",
                            }
                        }}
                    >
                        <ShLabel 
                            size={32} 
                            palette="normal"  
                            variant={isActive ? "green" : "red"}
                        >
                            {isActive ? t("connection_active") : t("connection_failed")}
                        </ShLabel>
                    </Box>
                ),
                afterChild:(
                    <Box 
                        sx={{
                            mt:3,
                            "@media screen and (min-width: 63.9375em)" : {
                                display:"none",
                            }
                        }}
                    >
                        <ShLabel 
                            size={32} 
                            palette="normal"  
                            variant={isActive ? "green" : "red"}
                        >
                            {isActive ? t("connection_active") : t("connection_failed")}
                        </ShLabel>
                    </Box>
                ),
                title: `${etailizeWebshopData.description}`,
                subTitle: [marketplaceName],
                icon: etailizeIcon,
                customIcon: match?.Icon ? <Box sx={{
                                borderRadius:3,
                                height:40,
                                overflow:'hidden',
                                width:40,
                                background:theme.palette.mode === "dark" ? colors.txt["primary"] : null,
                            }}
                        ><match.Icon height={40} width={40} /></Box> : null,
                options: [
                    { heading: t('description'), content: etailizeWebshopData.description },
                    { heading: t('status'), content: isActive ? t('active') : t('inactive') },
                    { heading: t('marketplace'), content: marketplaceName },
                    { heading: t('webshop_type'), content: etailizeWebshopData.webshop_type }
                ],
                actions: <SidePanelActions 
                    setIsExtended={setIsExtendedCallback} 
                    handleFilterPopoverClick={handleFilterPopoverClick}
                    etailizeWebshopId={props.etailizeWebshopId}
                    isRelation={isRelation}
                />
            }));
        }
    }, [etailizeWebshopData, isFetching, isLoading, t, setIsExtendedCallback, props.etailizeWebshopId]);

    // console.log("props.etailizeWebshopId",props.etailizeWebshopId);
    return (
        <>
            <Sidepanel2
                skeleton={isLoading || isFetching}
                tabConfig={config}
                content={panel} 
                isExtended={props.isExtended} 
                setIsExtended={props.setIsExtended} 
            >
                <>
                    <Details config={config.details} data={etailizeWebshopData} setIsExtended={props.setIsExtended}/>
                    <Sync config={config.sync} data={etailizeWebshopData}/>
                    <Orders config={config.orders} data={etailizeWebshopData} etailizeWebshopId={props.etailizeWebshopId}/>
                </>
            </Sidepanel2>
            
            {/* TODO: Add action modal for Etailize webshop actions */}
            {/* <AMEtailizeWebshopSp
                id={filterPopoverId}
                open={filterPopoverOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterPopoverClose}
                etailizeWebshopData={etailizeWebshopData}
            /> */}
        </>
    );
};

export default SpEtailizeWebshopDetails;